import { useContext } from 'react';

import { AuthContext } from '../../providers/AuthProvider';
import { ApiMethod } from '../models/api-types.type';
import type { ActivityKind, UsageDays, UsageOverview, VendorActivityPage, VendorPerson, VendorScope, VendorUsageDetail } from '../models/feature-usage.model';

/** pms-api-v2 src/feature-usage. */
export const FEATURE_USAGE_ROUTES = {
  overview: '/services/pms/admin/feature-usage',
  vendor: (vendorId: string) => `/services/pms/admin/feature-usage/vendors/${encodeURIComponent(vendorId)}`,
  /** Vendor portal: pages opened. */
  views: '/services/pms/feature-usage/views',
} as const;

export const useFeatureUsageApi = () => {
  const { sendAuthGuardedRequest } = useContext(AuthContext);
  const send = (method: ApiMethod, path: string, body?: any) => sendAuthGuardedRequest(method, path, body) as Promise<any>;
  return {
    overview: (days: UsageDays, scope: VendorScope, fresh = false): Promise<UsageOverview> =>
      send(ApiMethod.GET, `${FEATURE_USAGE_ROUTES.overview}?days=${days}&scope=${scope}${fresh ? '&fresh=true' : ''}`),
    vendor: (vendorId: string, days: UsageDays): Promise<VendorUsageDetail> => send(ApiMethod.GET, `${FEATURE_USAGE_ROUTES.vendor(vendorId)}?days=${days}`),
    /** What a vendor's team did, newest first; `before` pages back from the last item shown. */
    activity: (vendorId: string, params: { before?: string | null; limit?: number; kinds?: ActivityKind[]; user?: string }): Promise<VendorActivityPage> => {
      const q = new URLSearchParams();
      if (params.before) q.set('before', params.before);
      if (params.limit) q.set('limit', String(params.limit));
      if (params.kinds?.length) q.set('kinds', params.kinds.join(','));
      if (params.user) q.set('user', params.user);
      return send(ApiMethod.GET, `${FEATURE_USAGE_ROUTES.vendor(vendorId)}/activity?${q.toString()}`);
    },
    people: (vendorId: string): Promise<VendorPerson[]> => send(ApiMethod.GET, `${FEATURE_USAGE_ROUTES.vendor(vendorId)}/people`),
    recordViews: (views: { page: string; tab?: string; at: number }[]): Promise<{ recorded: number }> =>
      send(ApiMethod.POST, FEATURE_USAGE_ROUTES.views, { views }),
  };
};
